/**
 * VControl API Service
 * Handles all API calls for the VControl module including:
 * - Document management (upload, fetch, delete)
 * - Page image retrieval
 * - Project updates
 * - Chunked file uploads
 */

import { SERVER_URL } from '../DB_Url';
import { getAuthToken, clearAuthToken } from '../hooks/useAuthToken';

const API_BASE_URL = SERVER_URL;

class VControlAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Attach the Firebase auth token to a fetch call and handle 401 by
   * refreshing the token and retrying once (mirrors apiClient.js).
   * @param {string} url - The URL to fetch
   * @param {Object} options - Fetch options
   * @returns {Promise<Response>} The fetch response
   */
  async fetchWithAuth(url, options = {}) {
    const token = await getAuthToken();
    const headers = { ...(options.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(url, { ...options, headers });

    // 401 → refresh the token once and retry with the new token
    if (response.status === 401) {
      clearAuthToken();
      const newToken = await getAuthToken(true);
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        return fetch(url, { ...options, headers });
      }
    }

    return response;
  }

  /**
   * Helper to perform fetch with retry logic for intermittent network/CORS issues
   * @param {string} url - The URL to fetch
   * @param {Object} options - Fetch options
   * @param {number} retries - Number of retries (default: 2)
   * @param {number} backoff - Initial backoff delay in ms (default: 1000)
   */
  async fetchWithRetry(url, options = {}, retries = 2, backoff = 1000) {
    try {
      const response = await this.fetchWithAuth(url, options);

      // Successfully got a response
      if (response.ok) return response;

      // Handle specific retryable server errors (500s or timeouts)
      if (retries > 0 && (response.status >= 500 || response.status === 408)) {
        console.warn(
          `[API] Server error ${response.status} for ${url}. Retrying... (${retries} left)`,
        );
        await new Promise((resolve) => setTimeout(resolve, backoff));
        return this.fetchWithRetry(
          url,
          options,
          retries - 1,
          backoff * 2,
        );
      }

      return response;
    } catch (error) {
      // AbortError should not be retried
      if (error.name === 'AbortError') throw error;

      // Retry on network errors
      if (retries > 0) {
        console.warn(
          `[API] Network error for ${url}. Retrying... (${retries} left)`,
          error.message,
        );
        await new Promise((resolve) => setTimeout(resolve, backoff));
        return this.fetchWithRetry(
          url,
          options,
          retries - 1,
          backoff * 2,
        );
      }
      throw error;
    }
  }

  // ==================== DOCUMENT MANAGEMENT ====================

  /**
   * Fetch all documents for a specific user
   * @param {string} userUid - The user's UID
   * @returns {Promise<Array>} Array of user documents
   */
  async getUserDocuments(userUid) {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/user/documents?user_uid=${userUid}`,
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || 'Failed to fetch documents.',
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user documents:', error);
      throw error;
    }
  }

  /**
   * Fetch all documents for specific organizations
   * @param {Array<string>} organizations - Array of organization names
   * @returns {Promise<Array>} Array of organization documents
   */
  async getOrganizationDocuments(organizations) {
    try {
      const organizationsParam = Array.isArray(organizations)
        ? organizations.join(',')
        : organizations;

      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/organization/documents?organisations=${encodeURIComponent(organizationsParam)}`,
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error ||
          'Failed to fetch organization documents.',
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching organization documents:', error);
      throw error;
    }
  }

  /**
   * Fetch document / page structure for a project.
   * Returns the canonical list of pages (with version, documentId,
   * actualPageNumber, pageLabel, folderPath) used by the Replan page selector.
   * @param {string} projectName
   * @returns {Promise<Array>} Array of page entries
   */
  async getDocumentInfo(projectName) {
    if (!projectName) return [];
    try {
      const response = await this.fetchWithRetry(
        `${this.baseUrl}/api/document/info?project=${encodeURIComponent(projectName)}`,
      );
      if (!response.ok) {
        let message = 'Failed to fetch document info.';
        try {
          const errorData = await response.json();
          message = errorData.error || message;
        } catch {}
        throw new Error(message);
      }
      const data = await response.json();
      return Array.isArray(data?.pages) ? data.pages : [];
    } catch (error) {
      console.error('Error fetching document info:', error);
      throw error;
    }
  }

  /**
   * Backfill missing folderPath on documents from the per-project document-info
   * endpoint. Returns a new docs array; docs that already have folderPath are
   * returned unchanged.
   * @param {Array} docs - documents (each with documentId, projectName, folderPath?)
   * @param {string[]} projectNames - project names to fetch document info for
   * @returns {Promise<Array>} enriched docs
   */
  async enrichWithFolderPaths(docs, projectNames) {
    const folderByDocument = new Map();
    await Promise.all(
      projectNames.map(async (projectName) => {
        try {
          const pages = await this.getDocumentInfo(projectName);
          for (const page of pages) {
            if (page.documentId && page.folderPath && !folderByDocument.has(page.documentId)) {
              folderByDocument.set(page.documentId, page.folderPath);
            }
          }
        } catch (err) {
          console.warn(`Could not load document info for project "${projectName}"`, err);
        }
      }),
    );
    return docs.map((doc) =>
      !doc.folderPath && folderByDocument.has(doc.documentId)
        ? { ...doc, folderPath: folderByDocument.get(doc.documentId) }
        : doc,
    );
  }

  /**
   * Delete a document by ID
   * @param {string} documentId - The document ID
   * @returns {Promise<Object>} Response message
   */
  async deleteDocument(documentId) {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/document/${documentId}`,
        {
          method: 'DELETE',
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete document.');
      }

      return data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  // ==================== PAGE IMAGE RETRIEVAL ====================

  /**
   * Fetch a page image with optional region cropping
   * @param {Object} params - Request parameters
   * @param {string} params.documentId - The document ID
   * @param {number} params.pageNumber - The page number
   * @param {number} [params.x] - Optional X coordinate for region
   * @param {number} [params.y] - Optional Y coordinate for region
   * @param {number} [params.width] - Optional width for region
   * @param {number} [params.height] - Optional height for region
   * @returns {Promise<Blob>} Image blob
   */
  async getPageImage(
    documentId,
    pageNumber,
    { x, y, width, height } = {},
    options = {},
  ) {
    // --- PAGE IMAGE FETCH FLOW ---
    // 1. Build the API URL for the page image, optionally with crop params
    // 2. Fetch the image from backend (Python Flask)
    // 3. If backend returns a JSON with a 'url', fetch the image from B2 storage
    // 4. Return the image blob for use in frontend (VControl, Replan, etc)
    try {
      // Ensure base URL ends with a slash
      const base = this.baseUrl.endsWith('/')
        ? this.baseUrl
        : `${this.baseUrl}/`;
      let url = `${base}get-page-image?document_id=${documentId}&page_number=${pageNumber}`;

      const hasCrop =
        x !== undefined &&
        y !== undefined &&
        width !== undefined &&
        height !== undefined;
      if (hasCrop) {
        url += `&x=${Math.round(x)}&y=${Math.round(y)}&width=${Math.round(width)}&height=${Math.round(height)}`;
      }

      const response = await this.fetchWithRetry(url, {
        method: 'GET',
        signal: options?.signal,
      });

      if (!response.ok)
        throw new Error(
          `Failed to fetch page image for page ${pageNumber}`,
        );

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        if (data.url) {
          const imageResponse = await fetch(data.url, {
            mode: 'cors',
            signal: options?.signal,
          });
          if (!imageResponse.ok)
            throw new Error(
              `Failed to fetch image from B2 for page ${pageNumber}`,
            );
          return await imageResponse.blob();
        }
      }

      return await response.blob();
    } catch (error) {
      console.error('Error fetching page image:', error);
      throw error;
    }
  }

  // ==================== PROJECT UPDATES ====================

  /**
   * Update project information and page texts
   * @param {Object} projectData - Project data to update
   * @param {string} projectData.documentId - The document ID
   * @param {Object} projectData.projectInfo - Project information
   * @param {string} projectData.projectInfo.projectName - Project name
   * @param {string} projectData.projectInfo.versionName - Version name
   * @param {string} projectData.projectInfo.versionDate - Version date
   * @param {string} projectData.projectInfo.organisation - Organisation name
   * @param {Array} projectData.pages - Array of pages with texts
   * @returns {Promise<Object>} Response message
   */
  async updateProject({ documentId, projectInfo, pages }) {
    try {
      const response = await this.fetchWithAuth(`${this.baseUrl}/update-project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId,
          project_info: projectInfo,
          pages: pages.map((page) => ({
            pageNumber: page.pageNumber,
            texts: page.texts,
          })),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update project');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  }

  // ==================== CHUNKED UPLOAD ====================

  /**
   * Check the upload status of chunks for a specific page
   * @param {string} documentId - The document ID
   * @param {number} pageNumber - The page number
   * @returns {Promise<Object>} Upload status with chunks_uploaded array
   */
  async getUploadChunkStatus(documentId, pageNumber) {
    try {
      // ← define base HERE, not borrowed from another function
      const base = this.baseUrl.endsWith('/')
        ? this.baseUrl
        : this.baseUrl + '/';
      const response = await this.fetchWithAuth(
        `${base}upload-chunk-status?document_id=${documentId}&page_number=${pageNumber}`,
      );
      if (response.ok) return await response.json();
      return { chunks_uploaded: [] };
    } catch (error) {
      console.warn('getUploadChunkStatus failed:', error.message);
      return { chunks_uploaded: [] };
    }
  }

  /**
   * Check if the backend API server is reachable and return full health data
   * @returns {Promise<Object>} Full health payload from server + responseTime
   */
  async checkServerHealth() {
    const startTime = Date.now();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await this.fetchWithAuth(`${this.baseUrl}/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      if (!response.ok) {
        return {
          healthy: false,
          responseTime,
          status: 'degraded',
        };
      }

      const data = await response.json();
      return {
        healthy: data.status === 'ok',
        responseTime,
        status: data.status === 'ok' ? 'operational' : 'degraded',
        version: data.version || null,
        database: data.database || null,
        diskSpaceGb: data.disk_space_gb ?? null,
        diskStatus: data.disk_status || null,
        message: data.message || null,
        details: data,
      };
    } catch (error) {
      console.warn(
        '[API Health Check] Server unreachable:',
        error.message,
      );
      return {
        healthy: false,
        responseTime: Date.now() - startTime,
        status: 'unreachable',
      };
    }
  }

  /**
   * Upload a single chunk
   * @param {FormData} formData - FormData containing chunk and metadata
   * @returns {Promise<Object>} Upload response
   */
  async uploadChunk(formData) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 120s timeout for large chunks

      const response = await this.fetchWithAuth(`${this.baseUrl}/upload-chunk`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Try to parse error response
        let errorMessage = `Server error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // Response wasn't JSON, use status text
        }

        // Provide specific error messages for common issues
        if (response.status === 502 || response.status === 503) {
          throw new Error(
            `Backend server unavailable (${response.status}). Please check if the Python Flask server at ${this.baseUrl} is running.`,
          );
        } else if (response.status === 413) {
          throw new Error(
            'File chunk too large. Try reducing the chunk size.',
          );
        } else if (response.status === 500) {
          throw new Error(`Server error: ${errorMessage}`);
        }

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      // Distinguish between different error types
      if (error.name === 'AbortError') {
        throw new Error(
          'Upload timeout - chunk took too long to upload. Please check your connection.',
        );
      } else if (error.message.includes('Failed to fetch')) {
        throw new Error(
          `Cannot reach backend server at ${this.baseUrl}. Please check: 1) Server is running, 2) CORS is configured, 3) Network connection.`,
        );
      }

      console.error('Error uploading chunk:', error);
      throw error;
    }
  }

  /**
   * Calculate SHA-256 hash of a blob for integrity verification
   * @param {Blob} blob - The file blob to hash
   * @returns {Promise<string>} Hexadecimal hash string
   */
  async calculateFileHash(blob) {
    // Skipping client-side hash calculation.
    // Reading a 200MB+ Blob into an ArrayBuffer forces the browser to pull
    // the whole file into RAM, crashing low-end devices and freezing the UI.
    // TLS guarantees data transit integrity.
    return 'SKIP_HASH';
  }

  /**
   * Upload a file in chunks with retry logic and progress tracking
   * @param {Blob} blob - The file blob to upload
   * @param {Object} metadata - Upload metadata
   * @param {string} metadata.document_id - Document ID
   * @param {string} metadata.pdf_filename - Original PDF filename
   * @param {number} metadata.page_number - Page number
   * @param {string} metadata.dpi - DPI value
   * @param {string} metadata.uploader - Uploader UID
   * @param {Function} [metadata.onProgress] - Progress callback
   * @param {number} [chunkSize] - Size of each chunk (default: 256KB)
   * @returns {Promise<Object>} Upload result with success status and chunk count
   */
  async uploadFileInChunks(
    blob,
    metadata,
    chunkSize = 2 * 1024 * 1024,
  ) {
    const totalSize = blob.size;
    const totalChunks = Math.ceil(totalSize / chunkSize);

    // Calculate SHA-256 hash for integrity verification
    const fileHash = await this.calculateFileHash(blob);
    console.log(
      `File hash (SHA-256): ${fileHash}, size: ${totalSize} bytes`,
    );

    try {
      // 1. Check which chunks are already uploaded (resume capability)
      const status = await this.getUploadChunkStatus(
        metadata.document_id,
        metadata.page_number,
      );
      let uploadedChunks = status.chunks_uploaded || [];

      if (uploadedChunks.length > 0) {
        console.log(
          `Resuming upload: ${uploadedChunks.length}/${totalChunks} chunks already uploaded`,
        );
      }

      // 2. Upload missing chunks only
      for (let i = 0; i < totalChunks; i++) {
        // Skip if chunk already uploaded
        if (uploadedChunks.includes(i)) {
          console.log(
            `Skipping chunk ${i + 1}/${totalChunks} (already uploaded)`,
          );
          if (metadata.onProgress) {
            const progress = ((i + 1) / totalChunks) * 100;
            metadata.onProgress(progress);
          }
          continue;
        }

        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, totalSize);
        const chunk = blob.slice(start, end);

        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('document_id', metadata.document_id);
        formData.append('page_number', metadata.page_number);
        formData.append('chunk_index', i);
        formData.append('total_chunks', totalChunks);
        formData.append('file_hash', fileHash);
        formData.append('file_size', totalSize.toString());

        // Include metadata with the last chunk for reassembly
        if (i === totalChunks - 1) {
          formData.append('pdf_filename', metadata.pdf_filename);
          formData.append('dpi', metadata.dpi);
          formData.append('uploader', metadata.uploader);
        }

        // Retry logic for failed chunks
        let retries = 3;
        let uploaded = false;

        while (retries > 0 && !uploaded) {
          try {
            await this.uploadChunk(formData);
            uploaded = true;

            // Update progress
            if (metadata.onProgress) {
              const progress = ((i + 1) / totalChunks) * 100;
              metadata.onProgress(progress);
            }
          } catch (error) {
            retries--;
            if (retries === 0) {
              throw new Error(
                `Chunk ${i + 1}/${totalChunks} upload failed after 3 retries: ${error.message}`,
              );
            }
            console.warn(
              `Chunk ${i + 1} failed, retrying... (${retries} retries left)`,
            );
            // Wait before retry (exponential backoff)
            await new Promise((resolve) =>
              setTimeout(resolve, 1000 * (4 - retries)),
            );
          }
        }
      }

      return { success: true, chunks: totalChunks };
    } catch (error) {
      console.error('Chunked upload error:', error);
      throw error;
    }
  }

  /**
   * Upload the original PDF file to the same storage path as its page images.
   * Uses chunked upload for reliability with large files. No DB record is created.
   * @param {Blob} blob - The original PDF blob
   * @param {string} documentId - Document ID
   * @param {string} filename - Original PDF filename
   * @param {string} uploaderUid - Uploader UID
   * @param {Function} [onProgress] - Optional progress callback (0-100)
   * @returns {Promise<Object>} Upload result
   */

  async uploadOriginalPdf(
    blob,
    documentId,
    filename,
    uploaderUid,
    onProgress,
  ) {
    const chunkSize = 2 * 1024 * 1024; // 2MB safely clears the 4MB Nginx limit including multipart overhead
    const totalChunks = Math.ceil(blob.size / chunkSize);
    const CONCURRENCY = 4;

    console.log(
      `[uploadOriginalPdf] blob.size=${blob.size}, totalChunks=${totalChunks}, filename=${filename}`,
    );

    // Compute hash and check status in parallel — neither blocks the other
    const [fileHash, status] = await Promise.all([
      this.calculateFileHash(blob),
      this.getUploadChunkStatus(documentId, 0),
    ]);

    const uploadedChunks = new Set(status.chunks_uploaded || []);

    const uploadChunkAt = async (i) => {
      if (uploadedChunks.has(i)) return;
      const start = i * chunkSize;
      const chunk = blob.slice(
        start,
        Math.min(start + chunkSize, blob.size),
      );

      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('document_id', documentId);
      formData.append('page_number', 0);
      formData.append('chunk_index', i);
      formData.append('total_chunks', totalChunks);
      formData.append('file_hash', fileHash); // ← real hash on every chunk
      formData.append('file_size', blob.size.toString());

      if (i === totalChunks - 1) {
        formData.append('pdf_filename', filename);
        formData.append('uploader', uploaderUid);
        formData.append('file_type', 'pdf');
      }

      let retries = 3;
      while (retries > 0) {
        try {
          await this.uploadChunk(formData);
          break;
        } catch (e) {
          if (--retries === 0) throw e;
          await new Promise((r) =>
            setTimeout(r, 1000 * (4 - retries)),
          );
        }
      }
      if (onProgress)
        onProgress(Math.round(((i + 1) / totalChunks) * 100));
    };

    for (let i = 0; i < totalChunks; i += CONCURRENCY) {
      await Promise.all(
        Array.from(
          { length: Math.min(CONCURRENCY, totalChunks - i) },
          (_, j) => uploadChunkAt(i + j),
        ),
      );
    }
    // ← ADD: re-send last chunk to guarantee reassembly trigger
    const lastChunkStart = (totalChunks - 1) * chunkSize;
    const lastChunk = blob.slice(lastChunkStart, blob.size);
    const finalFormData = new FormData();
    finalFormData.append('chunk', lastChunk);
    finalFormData.append('document_id', documentId);
    finalFormData.append('page_number', 0);
    finalFormData.append('chunk_index', totalChunks - 1);
    finalFormData.append('total_chunks', totalChunks);
    finalFormData.append('file_hash', fileHash);
    finalFormData.append('file_size', blob.size.toString());
    finalFormData.append('pdf_filename', filename);
    finalFormData.append('uploader', uploaderUid);
    finalFormData.append('file_type', 'pdf');
    await this.uploadChunk(finalFormData);

    return { success: true };
  }

  // ==================== PDF PROCESSING ====================

  /**
   * Get PDF processing status from the server
   * @param {string} documentId
   * @param {string} uploaderUid
   */
  async getPdfProcessingStatus(documentId, uploaderUid) {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/document/${documentId}/processing-status?uploader_uid=${uploaderUid}`,
      );
      if (!response.ok) {
        throw new Error('Failed to fetch processing status');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching processing status:', error);
      throw error;
    }
  }

  /**
   * Manually trigger PDF processing (fallback)
   */
  async triggerPdfProcessing(documentId, pdfFilename, uploaderUid) {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/document/process-pdf`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            document_id: documentId,
            pdf_filename: pdfFilename,
            uploader_uid: uploaderUid,
          }),
        },
      );
      if (!response.ok)
        throw new Error('Failed to trigger PDF processing');
      return await response.json();
    } catch (error) {
      console.error('Error triggering processing:', error);
      throw error;
    }
  }

  // ==================== OCR SEARCH ====================

  /**
   * Search for text within a specific document (all pages)
   * @param {string} documentId - The document ID to search within
   * @param {string} query - Search query (minimum 2 characters)
   * @returns {Promise<Object>} Search results with text matches and coordinates
   */
  async searchDocumentText(documentId, query) {
    try {
      if (!query || query.length < 2) {
        throw new Error('Search query must be at least 2 characters');
      }

      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/document/${documentId}/search?q=${encodeURIComponent(query)}`,
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || 'Failed to search document text',
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching document text:', error);
      throw error;
    }
  }

  /**
   * Search for text within a specific page
   * @param {string} documentId - The document ID
   * @param {number} pageNumber - The page number to search within
   * @param {string} query - Search query (minimum 2 characters)
   * @returns {Promise<Object>} Search results with text matches, positions, and occurrence count
   */
  async searchPageText(documentId, pageNumber, query) {
    try {
      if (!query || query.length < 2) {
        throw new Error('Search query must be at least 2 characters');
      }

      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/document/${documentId}/page/${pageNumber}/search?q=${encodeURIComponent(query)}`,
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || 'Failed to search page text',
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching page text:', error);
      throw error;
    }
  }

  /**
   * Search for text across all documents in a project
   * @param {string} projectName - The project name to search within
   * @param {string} query - Search query (minimum 2 characters)
   * @returns {Promise<Object>} Search results grouped by document and page
   */
  async searchProjectText(projectName, query) {
    try {
      if (!query || query.length < 2) {
        throw new Error('Search query must be at least 2 characters');
      }

      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/project/${encodeURIComponent(projectName)}/search?q=${encodeURIComponent(query)}`,
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || 'Failed to search project text',
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching project text:', error);
      throw error;
    }
  }

  /**
   * Get OCR processing status for a document
   * @param {string} documentId - The document ID
   * @returns {Promise<Object>} OCR processing status (pending, processing, completed, failed)
   */
  async getDocumentOcrStatus(documentId) {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/document/${documentId}/ocr-status`,
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || 'Failed to fetch OCR status',
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching OCR status:', error);
      throw error;
    }
  }

  /**
   * Get OCR processing status for a specific page
   * @param {string} documentId - The document ID
   * @param {number} pageNumber - The page number
   * @returns {Promise<Object>} Page OCR status with text availability info
   */
  async getPageOcrStatus(documentId, pageNumber) {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/document/${documentId}/page/${pageNumber}/ocr-status`,
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || 'Failed to fetch page OCR status',
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching page OCR status:', error);
      throw error;
    }
  }

  /**
   * Get all extracted text for a specific page
   * @param {string} documentId - The document ID
   * @param {number} pageNumber - The page number
   * @returns {Promise<Object>} Page text with text blocks and coordinates
   */
  async getPageText(documentId, pageNumber, options = {}) {
    try {
      const response = await this.fetchWithRetry(
        `${this.baseUrl}/api/document/${documentId}/page/${pageNumber}/text`,
        { signal: options.signal },
      );

      if (!response.ok) {
        // If 404, the text might just not be ready yet or doesn't exist
        if (response.status === 404) {
          return { textBlocks: [], status: 'not_found' };
        }

        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to fetch page text (Status: ${response.status})`,
        );
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') return { textBlocks: [], aborted: true };
      console.error('Error fetching page text:', error);
      throw error;
    }
  }
  /**
   * Upload extracted text blocks with coordinates for a specific page
   * @param {string} documentId - The document ID
   * @param {number} pageNumber - The page number
   * @param {Array} textBlocks - Array of {text, x, y, width, height}
   * @returns {Promise} Response message
   */
  async uploadPageText(documentId, pageNumber, textBlocks, totalPages = 0, clear = true) {
    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/api/document/${documentId}/page/${pageNumber}/text`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            text_blocks: textBlocks,
            total_pages: totalPages,
            clear: clear
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload page text');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading page text:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== THUMBNAIL RETRIEVAL ====================

  /**
   * Fetch a thumbnail image for a page with exponential backoff retry logic
   * @param {string} documentId - The document ID
   * @param {number} pageNumber - The page number
   * @param {Object} options - Options including signal for abort
   * @returns {Promise<Blob>} Thumbnail image blob
   */
  async getThumbnail(documentId, pageNumber, options = {}) {
    // Exponential backoff delays: 2s, 10s, 30s, 60s
    const retryDelays = [2000, 10000, 30000, 60000];
    let attempt = 0;

    const attemptFetch = async () => {
      try {
        const url = `${this.baseUrl}/api/thumbnail?document_id=${documentId}&page_number=${pageNumber}`;
        const metaResponse = await this.fetchWithAuth(url, {
          signal: options.signal,
        });

        const data = await metaResponse.json();

        // Handle pending status (202 Accepted) - wait and retry
        if (data.status === 'pending') {
          if (attempt >= retryDelays.length) {
            throw new Error(
              `Thumbnail generation timeout for page ${pageNumber} after ${retryDelays.length} retries`,
            );
          }

          const waitTime = retryDelays[attempt];
          attempt++;

          // Wait before retrying
          await new Promise((resolve) =>
            setTimeout(resolve, waitTime),
          );
          return attemptFetch(); // Recursive retry
        }

        // Handle ready status (200 OK)
        if (data.status === 'ready') {
          if (!data.url) {
            throw new Error(
              `No thumbnail URL provided by backend for page ${pageNumber}`,
            );
          }

          const imageResponse = await fetch(data.url, {
            signal: options.signal,
            mode: 'cors',
          });
          if (!imageResponse.ok)
            throw new Error(
              `Failed to fetch thumbnail image for page ${pageNumber}`,
            );
          return await imageResponse.blob();
        }

        // Handle unknown status
        throw new Error(
          `Unknown thumbnail status "${data.status}" for page ${pageNumber}`,
        );
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        throw error;
      }
    };

    try {
      return await attemptFetch();
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      console.error('Error fetching thumbnail:', error);
      throw error;
    }
  }
}

// Export a singleton instance
const vcontrolAPI = new VControlAPI();
export default vcontrolAPI;

// Also export the class for testing or custom instances
export { VControlAPI };
